import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Row, Col, Form, Alert } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import axios from 'axios';
import countries from "./Countries";
import states from "./States";
import { headers } from '../../../helpers/Requests';
import { baseURL } from '../../../helpers/baseURL';

const parseCardState = (card) => {
	if (card?.state && typeof card.state === 'string' && card.state.includes(', ')) {
		const parts = card.state.split(', ');
		return {
			state: parts[0].trim(),
			country: parts[parts.length - 1].trim(),
		};
	}
	if (card?.country && typeof card.country === 'string' && card.country.length === 2) {
		return {
			state: card.state || '',
			country: card.country,
		};
	}
	return { state: card?.state?.trim() || '', country: 'US' };
};

const buildFormFromCard = (card) => {
	if (!card) return null;
	const { state, country } = parseCardState(card);
	const month = String(card.expiration_month || '').padStart(2, '0');
	const year = String(card.expiration_year || '').slice(-2);

	return {
		name_on_card: card.name_on_card || '',
		expiration_date: month && year ? `${month}/${year}` : '',
		country: country || 'US',
		city: card.city || '',
		street: card.street || '',
		state,
		zip_code: card.zip_code || '',
	};
};

const EditCard = ({ card, show, onHide, onUpdated }) => {
	const [validated, setValidated] = useState(false);
	const [form, setForm] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [errorMessage, setErrorMessage] = useState('');
	const [availableStates, setAvailableStates] = useState(states['US']);

	useEffect(() => {
		if (show && card) {
			setForm(buildFormFromCard(card));
			setErrors({});
			setErrorMessage('');
			setValidated(false);
		}
	}, [show, card]);

	useEffect(() => {
		if (!form) return;
		if (form.country === 'US' || form.country === 'CA') {
			setAvailableStates(states[form.country]);
		} else {
			setAvailableStates(null);
		}
	}, [form?.country]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const hideModal = () => {
		setErrors({});
		setErrorMessage('');
		setValidated(false);
		setForm(null);
		onHide();
	};

	const updatePaymentMethod = () => {
		if (!card?.id || !form) return;
		setValidated(false);
		setIsLoading(true);
		axios.put(`${baseURL}payment-methods/${card.id}`, form, headers)
			.then(() => {
				onUpdated?.();
				hideModal();
			})
			.catch((error) => {
				if (error.response?.data) {
					if (error.response.data.errors) {
						setErrors(error.response.data.errors);
					}
					if (error.response.data.message) {
						setErrorMessage(error.response.data.message);
					}
				} else {
					console.error(error);
					setErrorMessage('Something went wrong. Please try again later.');
				}
				setValidated(true);
			})
			.finally(() => setIsLoading(false));
	};

	const lastFour = card?.last_four_numbers
		|| card?.last_four
		|| (card?.card_number ? String(card.card_number).slice(-4) : '');

	if (!form) {
		return null;
	}

	return (
		<Modal show={show} onHide={hideModal}>
			<Modal.Header closeButton>
				<Modal.Title>Update Payment Method</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form>
					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
					<fieldset disabled={isLoading}>
						<Form.Group className="mb-2">
							<Form.Label>Card Number</Form.Label>
							<Form.Control
								type="text"
								value={lastFour ? `****-****-****-${lastFour}` : '****-****-****-****'}
								readOnly
								disabled
							/>
							<Form.Text className="text-muted">
								For security, the card number cannot be changed. Add a new card if you need to use a different number.
							</Form.Text>
						</Form.Group>
						<Form.Group controlId="editPaymentName" className="mb-1">
							<Form.Label>Cardholder Name:</Form.Label>
							<Form.Control
								value={form.name_on_card}
								name="name_on_card"
								onChange={handleChange}
								type="text"
								placeholder="Name on card"
								required
								isInvalid={!!errors.name_on_card}
							/>
							{errors.name_on_card && (
								<Form.Control.Feedback type="invalid">{errors.name_on_card[0]}</Form.Control.Feedback>
							)}
						</Form.Group>
						<Row>
							<Col sm="6">
								<Form.Group controlId="editExpiryDate" className="pt-2 mb-1">
									<Form.Label>Expiration Date:</Form.Label>
									<InputMask
										value={form.expiration_date}
										name="expiration_date"
										onChange={handleChange}
										mask="99/99"
										placeholder="MM/YY"
									>
										{(inputProps) => (
											<Form.Control {...inputProps} type="text" required isInvalid={!!errors.expiration_date} />
										)}
									</InputMask>
									{errors.expiration_date && (
										<Form.Control.Feedback type="invalid">{errors.expiration_date[0]}</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>
						</Row>
						<Form.Group controlId="editStreet" className="pt-2 mb-1">
							<Form.Label>Street:</Form.Label>
							<Form.Control
								value={form.street}
								name="street"
								onChange={handleChange}
								type="text"
								placeholder="Street"
								required
								isInvalid={!!errors.street}
							/>
							{errors.street && (
								<Form.Control.Feedback type="invalid">{errors.street[0]}</Form.Control.Feedback>
							)}
						</Form.Group>
						<Row>
							<Col sm="6">
								<Form.Group controlId="editCity" className="pt-2 mb-1">
									<Form.Label>City:</Form.Label>
									<Form.Control
										value={form.city}
										name="city"
										onChange={handleChange}
										type="text"
										placeholder="City"
										required
										isInvalid={!!errors.city}
									/>
									{errors.city && (
										<Form.Control.Feedback type="invalid">{errors.city[0]}</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>
							<Col sm="6">
								<Form.Group controlId="editState" className="pt-2 mb-1">
									<Form.Label>State:</Form.Label>
									{availableStates ? (
										<Form.Control
											as="select"
											value={form.state}
											name="state"
											onChange={handleChange}
											required
											isInvalid={!!errors.state}
										>
											<option value="">Select a state</option>
											{availableStates.map((stateOption, index) => (
												<option value={stateOption.code} key={index}>{stateOption.name}</option>
											))}
										</Form.Control>
									) : (
										<Form.Control
											value={form.state}
											name="state"
											onChange={handleChange}
											type="text"
											placeholder="State"
											required
											isInvalid={!!errors.state}
										/>
									)}
									{errors.state && (
										<Form.Control.Feedback type="invalid">{errors.state[0]}</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>
						</Row>
						<Row>
							<Col sm="6">
								<Form.Group controlId="editZip" className="pt-2 mb-1">
									<Form.Label>Zip Code:</Form.Label>
									<Form.Control
										value={form.zip_code}
										name="zip_code"
										onChange={handleChange}
										type="text"
										placeholder="Zip Code"
										required
										isInvalid={!!errors.zip_code}
									/>
									{errors.zip_code && (
										<Form.Control.Feedback type="invalid">{errors.zip_code[0]}</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>
							<Col sm="6">
								<Form.Group controlId="editCountry" className="pt-2 mb-1">
									<Form.Label>Country:</Form.Label>
									<Form.Control
										as="select"
										value={form.country}
										name="country"
										onChange={handleChange}
										required
										isInvalid={!!errors.country}
									>
										{countries.map((country, index) => (
											<option value={country.code} key={index}>{country.name}</option>
										))}
									</Form.Control>
									{errors.country && (
										<Form.Control.Feedback type="invalid">{errors.country[0]}</Form.Control.Feedback>
									)}
								</Form.Group>
							</Col>
						</Row>
					</fieldset>
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={hideModal} disabled={isLoading}>
					Close
				</Button>
				<Button variant="primary" onClick={updatePaymentMethod} disabled={isLoading}>
					{isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default EditCard;
